"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from "@mui/material";
import {
  Plus,
  TrendingUp,
  Package,
  BarChart3,
  Edit2,
  Trash2,
  CreditCard,
  Activity,
  Clock,
  Check,
} from "lucide-react";
import apiEndpoints from "../../apiconfig";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Tab panel helper component for better tab management
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Reusable Card Component for Payment Stats
function PaymentStatCard({ label, value, color, growth, Icon }) {
  return (
    <Grid item sx={{ width: { xs: "100%", sm: "48%", md: "23%", lg: "22%" } }}>
      <Paper
        sx={{
          borderLeft: `6px solid ${color}`,
          borderRadius: "16px",
          p: 3,
          height: "140px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="600">
              {label}
            </Typography>
            <Typography variant="h5" fontWeight="700" sx={{ mt: 1 }}>
              {value}
            </Typography>
            {growth && (
              <Typography
                variant="caption"
                color="success.main"
                display="flex"
                alignItems="center"
                mt={1}
              >
                <TrendingUp size={14} />
                &nbsp;+{growth}% from last month
              </Typography>
            )}
            {!growth && (
              <Typography
                variant="caption"
                color="text.secondary"
                mt={1}
                display="block"
              >
                Active & Updated
              </Typography>
            )}
          </Box>
          {Icon && <Icon size={32} color={color} />}
        </Box>
      </Paper>
    </Grid>
  );
}

export default function PaymentManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [state, setState] = useState({
    isManualPaymentDialogOpen: false,
    isAssignPackageDialogOpen: false,
    manualPayment: {
      userId: "",
      userName: "",
      db_id: "",
      planId: "",
      amount: "",
      paymentMethod: "UPI",
      transactionId: "",
      notes: "",
      expiryDate: "",
    },
    packageAssignment: {
      userId: "",
      userName: "",
      db_id: "",
      planId: "",
    },
    subscriptionPlans: [],
    recentPayments: [],
    expiringSubscriptions: [],
    revenueData: [],
    monthlyStats: {
      totalRevenue: 0,
      growthRate: 12,
      totalSubscriptions: 0,
      averageRevenuePerUser: 0,
    },
    isLoading: true,
    isFetchingUser: false,
    planDialog: {
      open: false,
      isEdit: false,
      data: {
        id: "",
        name: "",
        price: "",
        duration_months: 1,
        features: "",
        status: "active",
        credits: 0,
      },
    },
    snackbar: { open: false, message: "", severity: "success" },
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Unified form field styling for clean alignment
  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderRadius: "10px" },
      "&.Mui-focused fieldset": { borderColor: "#000000" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
  };

  // --- API LOGIC: Fetch Data ---
  const fetchData = async () => {
    try {
      const planRes = await fetch(
        `${apiEndpoints.subscriptionPlans}?action=list`,
      );
      const planData = await planRes.json();
      const payRes = await fetch(`${apiEndpoints.payments}?action=list`);
      const payData = await payRes.json();

      if (payData.status === "success") {
        setState((prev) => ({
          ...prev,
          subscriptionPlans: planData.plans || [],
          recentPayments: payData.payments || [],
          expiringSubscriptions: payData.expiring || [],
          revenueData: payData.revenueData || [],
          monthlyStats: payData.stats || prev.monthlyStats,
          isLoading: false,
        }));
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- LOGIC: User Lookup (generates Registered Name from ID) ---
  const handleUserIdLookup = async (val, formType) => {
    setState((p) => ({ ...p, [formType]: { ...p[formType], userId: val } }));
    if (val.length < 2) return;
    setState((prev) => ({ ...prev, isFetchingUser: true }));
    try {
      const response = await fetch(
        `${apiEndpoints.payments}?action=getUserName&id=${val}`,
      );
      const data = await response.json();
      if (data.status === "success") {
        setState((prev) => ({
          ...prev,
          isFetchingUser: false,
          [formType]: {
            ...prev[formType],
            userName: data.name,
            db_id: data.db_id,
          },
        }));
      } else {
        setState((prev) => ({
          ...prev,
          isFetchingUser: false,
          [formType]: {
            ...prev[formType],
            userName: "User not found",
            db_id: "",
          },
        }));
      }
    } catch (e) {
      setState((prev) => ({ ...prev, isFetchingUser: false }));
    }
  };

  // --- LOGIC: Renew click ---
  const handleRenewClick = (user) => {
    setState((prev) => ({
      ...prev,
      isAssignPackageDialogOpen: true,
      packageAssignment: {
        ...prev.packageAssignment,
        userId: user.memberDisplayId,
        userName: user.userName,
        db_id: user.id,
      },
    }));
  };

  // --- LOGIC: Form Submissions ---
  const handleManualPaymentSubmit = async () => {
    if (!state.manualPayment.db_id)
      return showSnackbar("Enter a valid User ID", "error");
    try {
      const response = await fetch(apiEndpoints.payments, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "insert",
          ...state.manualPayment,
          userId: state.manualPayment.db_id,
        }),
      });
      const data = await response.json();
      if (data.status === "success") {
        showSnackbar(data.message);
        fetchData();
        setState((prev) => ({ ...prev, isManualPaymentDialogOpen: false }));
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (e) {
      showSnackbar("Connection error", "error");
    }
  };

  // --- LOGIC: Assign Package Submission ---
  const handleAssignPackageSubmit = async () => {
    // Validate that a user and a plan have been selected
    if (!state.packageAssignment.db_id)
      return showSnackbar("Please enter a valid User ID", "error");
    if (!state.packageAssignment.planId)
      return showSnackbar("Please select a subscription plan", "error");

    try {
      const response = await fetch(apiEndpoints.payments, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "assign_package",
          userId: state.packageAssignment.db_id, // Internal primary key of the user
          planId: state.packageAssignment.planId, // The ID of the selected plan
        }),
      });

      const data = await response.json();
      if (data.status === "success") {
        showSnackbar("Package assigned and user record updated!");
        fetchData(); // Refresh table data
        setState((prev) => ({ ...prev, isAssignPackageDialogOpen: false }));
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (e) {
      showSnackbar("Connection error", "error");
    }
  };

  const handlePlanAction = async (action, planData) => {
    try {
      const response = await fetch(apiEndpoints.subscriptionPlans, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...planData }),
      });
      const data = await response.json();
      if (data.status === "success") {
        showSnackbar(data.message);
        fetchData();
        setState((prev) => ({
          ...prev,
          planDialog: { ...prev.planDialog, open: false },
        }));
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      showSnackbar("Something went wrong", "error");
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "N/A";
  const showSnackbar = (message, severity = "success") =>
    setState((prev) => ({
      ...prev,
      snackbar: { open: true, message, severity },
    }));
  const handleSnackbarClose = () =>
    setState((prev) => ({
      ...prev,
      snackbar: { ...prev.snackbar, open: false },
    }));
  const openPlanDialog = (p = null) =>
    setState((prev) => ({
      ...prev,
      planDialog: {
        open: true,
        isEdit: !!p,
        data: p || {
          id: "",
          name: "",
          price: "",
          duration_months: 1,
          features: "",
          status: "active",
        },
      },
    }));

  const {
    recentPayments,
    expiringSubscriptions,
    revenueData,
    monthlyStats,
    subscriptionPlans,
  } = state;

  if (state.isLoading)
    return (
      <Box
        p={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress sx={{ color: "#000" }} />
      </Box>
    );

  return (
    <Box
      sx={{ width: "100%", pb: 4 }}
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {/* Page Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight="700">
            Payment Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time revenue tracking and subscription control
          </Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={<Package size={18} />}
            onClick={() =>
              setState((p) => ({ ...p, isAssignPackageDialogOpen: true }))
            }
            sx={{
              borderColor: "#e0e0e0",
              color: "#000",
              borderRadius: "10px",
              fontWeight: "600",
              "&:hover": {
                borderColor: "#e0e0e0",
                backgroundColor: "transparent",
                color: "#000",
              },
            }}
          >
            Assign Package
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() =>
              setState((p) => ({ ...p, isManualPaymentDialogOpen: true }))
            }
            sx={{
              backgroundColor: "#ffe599",
              color: "#000",
              borderRadius: "10px",
              fontWeight: "600",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#ffe599", boxShadow: "none" },
            }}
          >
            Manual Payment
          </Button>
        </Box>
      </Box>

      {/* Analytics Cards */}
      <Grid container spacing={3} width={"100%"} justifyContent="space-between">
        <PaymentStatCard
          label="Total Revenue"
          value={formatCurrency(monthlyStats.totalRevenue)}
          color="#16a34a"
          growth={monthlyStats.growthRate}
          Icon={CreditCard}
        />
        <PaymentStatCard
          label="Active Subscriptions"
          value={monthlyStats.totalSubscriptions}
          color="#2563eb"
          Icon={Activity}
        />
        <PaymentStatCard
          label="Avg Revenue/User"
          value={formatCurrency(monthlyStats.averageRevenuePerUser)}
          color="#7c3aed"
          Icon={BarChart3}
        />
        <PaymentStatCard
          label="Expiring Soon"
          value={expiringSubscriptions.length}
          color="#ca8a04"
          Icon={Clock}
        />
      </Grid>

      {/* Dynamic Tabs */}
      <Paper sx={{ borderRadius: "16px", overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "#eee", px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontFamily: "Montserrat, sans-serif",
                minHeight: 48,
                color: "text.secondary",
              },
              "& .Mui-selected": {
                color: "#b8860b !important",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#b8860b",
                height: "3px",
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTabs-scrollButtons": {
                color: "#b8860b",
              },
            }}
          >
            <Tab label="Revenue Trends" />
            <Tab label="Subscription Plans" />
            <Tab label="Recent Transactions" />
            <Tab label="Expiring Soon" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          <TabPanel value={activeTab} index={0}>
            <Box height={350} width="100%">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2563eb"
                    strokeWidth={4}
                    dot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              gap={1}
            >
              <Box>
                <Typography variant="h5" fontWeight="800">
                  Subscription Plans
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your pricing tiers and features
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => openPlanDialog()}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  borderRadius: "12px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: "600",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  "&:hover": {
                    bgcolor: "#222",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                  },
                }}
              >
                Create New Plan
              </Button>
            </Box>
            <Grid
              container
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              width="100%"
            >
              {subscriptionPlans.map((plan) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={plan.id}
                  sx={{
                    display: "flex",
                    width: { xs: "100%", sm: "45%", md: "30%" },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      width: "100%",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "divider",
                      overflow: "hidden",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    {/* Active Badge */}
                    {plan.status === "active" && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          background: "rgba(22, 163, 74, 0.1)",
                          color: "#16a34a",
                          px: 1.2,
                          py: 0.4,
                          borderRadius: "100px",
                          fontSize: "0.7rem",
                          fontWeight: "700",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        Active
                      </Box>
                    )}

                    {/* Header Section */}
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        borderBottom: "1px solid",
                        borderColor: "grey.50",
                        background:
                          "linear-gradient(180deg, #fff 0%, #fafafa 100%)",
                      }}
                    >
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        fontWeight="800"
                        letterSpacing="1.5px"
                        sx={{ fontSize: "0.75rem", display: "block", mb: 1 }}
                      >
                        {plan.name}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="baseline"
                      >
                        <Typography
                          variant="h4"
                          fontWeight="800"
                          sx={{ color: "text.primary" }}
                        >
                          {formatCurrency(plan.price)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="600"
                          sx={{ ml: 0.5 }}
                        >
                          / {plan.duration_months} mo
                        </Typography>
                      </Box>
                    </Box>

                    {/* Features Section */}
                    <Box
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {/* Credits Highlight */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        sx={{
                          bgcolor: "#fffbeb",
                          py: 1,
                          borderRadius: "10px",
                          border: "1px dashed #fcd34d",
                          mb: 1,
                        }}
                      >
                        <CreditCard size={14} color="#d97706" />
                        <Typography
                          variant="subtitle2"
                          fontWeight="700"
                          color="#b45309"
                        >
                          {plan.credits} Credits
                        </Typography>
                      </Box>

                      <Box display="flex" flexDirection="column" gap={1.5}>
                        {plan.features?.split(",").map((feature, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            alignItems="flex-start"
                            gap={1.5}
                          >
                            <Check
                              size={16}
                              color="#16a34a"
                              strokeWidth={3}
                              style={{ marginTop: 3, flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.5, fontSize: "0.875rem" }}
                            >
                              {feature.trim()}
                            </Typography>
                          </Box>
                        ))}
                        {(!plan.features || plan.features.length === 0) && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            textAlign="center"
                            display="block"
                            fontStyle="italic"
                            mt={2}
                          >
                            Standard features apply
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Actions Footer */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#fafafa",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1.5,
                      }}
                    >
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={<Edit2 size={14} />}
                        onClick={() => openPlanDialog(plan)}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: "600",
                          borderColor: "grey.300",
                          color: "text.primary",
                          fontSize: "0.80rem",
                          "&:hover": {
                            borderColor: "primary.main",
                            color: "primary.main",
                            bgcolor: "primary.50",
                          },
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={<Trash2 size={14} />}
                        onClick={() => {
                          if (window.confirm("Delete plan?"))
                            handlePlanAction("delete", { id: plan.id });
                        }}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: "600",
                          borderColor: "grey.200", // subtler border
                          color: "error.main",
                          fontSize: "0.80rem",
                          "&:hover": {
                            borderColor: "error.main",
                            bgcolor: "error.50",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* Table mappings from state arrays */}
          <TabPanel value={activeTab} index={2}>
            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h5" fontWeight="800">
                  Recent Transactions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Latest financial activities and payments
                </Typography>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "grey.200",
                overflowX: { xs: "auto", md: "visible" },
              }}
            >
              <Table>
                <TableHead sx={{ bgcolor: "#fafafa" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Member ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      User
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentPayments.length > 0 ? (
                    recentPayments.map((p) => (
                      <TableRow
                        key={p.id}
                        hover
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          sx={{ fontWeight: "600", fontSize: "0.9rem" }}
                        >
                          #{p.memberDisplayId || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.85rem",
                                bgcolor: "#f3f4f6",
                                color: "#1f2937",
                                fontWeight: "700",
                              }}
                            >
                              {p.userName?.charAt(0).toUpperCase() || "U"}
                            </Avatar>
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="text.primary"
                            >
                              {p.userName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="700">
                            {formatCurrency(p.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.8,
                              bgcolor:
                                p.status === "success" ? "#dcfce7" : "#fef9c3",
                              color:
                                p.status === "success" ? "#166534" : "#854d0e",
                              px: 1.2,
                              py: 0.4,
                              borderRadius: "100px",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              textTransform: "uppercase",
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: "currentColor",
                              }}
                            />
                            {p.status}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.85rem"
                          >
                            {formatDate(p.payment_date)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={1}
                          color="text.secondary"
                        >
                          <CreditCard size={40} strokeWidth={1.5} />
                          <Typography variant="body1" fontWeight="500">
                            No recent transactions found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#fafafa" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Member ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      User Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Expiry Date
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "700",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        py: 2,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expiringSubscriptions.map((sub) => (
                    <TableRow key={sub.id} hover>
                      <TableCell>{sub.memberDisplayId}</TableCell>
                      <TableCell>{sub.userName}</TableCell>
                      <TableCell
                        sx={{ color: "error.main", fontWeight: "600" }}
                      >
                        {formatDate(sub.expiryDate)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": { bgcolor: "#000" },
                          }}
                          onClick={() => handleRenewClick(sub)}
                        >
                          Renew
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Box>
      </Paper>
      {/* Standardized DIALOG: Manual Payment */}
      <Dialog
        open={state.isManualPaymentDialogOpen}
        onClose={() =>
          setState((p) => ({ ...p, isManualPaymentDialogOpen: false }))
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "20px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "800",
            fontSize: "1.5rem",
            pb: 1,
          }}
        >
          Add Manual Payment
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}
          >
            {/* Row 1: User ID and Amount */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <TextField
                label="User ID (MIT...)"
                fullWidth
                variant="outlined"
                size="small"
                value={state.manualPayment.userId}
                onChange={(e) =>
                  handleUserIdLookup(e.target.value, "manualPayment")
                }
              />
              <TextField
                label="Amount (₹)"
                fullWidth
                type="number"
                variant="outlined"
                size="small"
                value={state.manualPayment.amount || ""}
                onChange={(e) =>
                  setState((p) => ({
                    ...p,
                    manualPayment: {
                      ...p.manualPayment,
                      amount: e.target.value,
                    },
                  }))
                }
                InputProps={{
                  inputProps: { min: 1, step: 1 },
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Row 2: Payment Method and Transaction ID */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="payment-method-label">
                  Payment Method
                </InputLabel>
                <Select
                  labelId="payment-method-label"
                  value={state.manualPayment.paymentMethod || ""}
                  label="Payment Method"
                  onChange={(e) =>
                    setState((p) => ({
                      ...p,
                      manualPayment: {
                        ...p.manualPayment,
                        paymentMethod: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="Card">Card</MenuItem>
                  <MenuItem value="Net Banking">Net Banking</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Transaction ID"
                fullWidth
                variant="outlined"
                size="small"
                value={state.manualPayment.transactionId || ""}
                onChange={(e) =>
                  setState((p) => ({
                    ...p,
                    manualPayment: {
                      ...p.manualPayment,
                      transactionId: e.target.value,
                    },
                  }))
                }
              />
            </Box>

            {/* Row 3: Registered Name and Expiry Date */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <TextField
                label="Registered Name"
                fullWidth
                variant="outlined"
                size="small"
                disabled
                value={state.manualPayment.userName}
                InputProps={{
                  endAdornment: state.isFetchingUser && (
                    <CircularProgress size={20} />
                  ),
                }}
              />
              <TextField
                label="Expiry Date"
                fullWidth
                type="date"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={state.manualPayment.expiryDate || ""}
                onChange={(e) =>
                  setState((p) => ({
                    ...p,
                    manualPayment: {
                      ...p.manualPayment,
                      expiryDate: e.target.value,
                    },
                  }))
                }
              />
            </Box>

            {/* Notes - Full Width */}
            <TextField
              label="Notes / Reference"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              value={state.manualPayment.notes || ""}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  manualPayment: {
                    ...p.manualPayment,
                    notes: e.target.value,
                  },
                }))
              }
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() =>
              setState((p) => ({ ...p, isManualPaymentDialogOpen: false }))
            }
            variant="outlined"
            sx={{
              color: "#666",
              borderColor: "#ddd",
              textTransform: "none",
              px: 3,
              py: 0.8,
              "&:hover": {
                borderColor: "#ddd",
                backgroundColor: "transparent",
                color: "#666",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            sx={{
              bgcolor: "#000",
              color: "#fff",
              borderRadius: "8px",
              px: 4,
              py: 0.8,
              textTransform: "none",
              "&:hover": { bgcolor: "#000" },
            }}
            onClick={handleManualPaymentSubmit}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Standardized DIALOG: Assign Package */}
      <Dialog
        open={state.isAssignPackageDialogOpen}
        onClose={() =>
          setState((p) => ({ ...p, isAssignPackageDialogOpen: false }))
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.5rem",
            pb: 1,
          }}
        >
          Assign Membership Package
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderTop: "1px solid #eaeef2",
            borderBottom: "1px solid #eaeef2",
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 1 }}
          >
            {/* User ID and Name */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <TextField
                label="User ID"
                fullWidth
                variant="outlined"
                size="small"
                value={state.packageAssignment.userId}
                onChange={(e) =>
                  handleUserIdLookup(e.target.value, "packageAssignment")
                }
              />

              <TextField
                label="User Name"
                fullWidth
                variant="outlined"
                size="small"
                disabled
                value={state.packageAssignment.userName}
              />
            </Box>

            {/* Select Plan and Duration */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="plan-select-label">Select Plan</InputLabel>

                <Select
                  labelId="plan-select-label"
                  id="plan-select"
                  value={state.packageAssignment.planId ?? ""}
                  label="Select Plan"
                  onChange={(e) => {
                    setState((p) => ({
                      ...p,
                      packageAssignment: {
                        ...p.packageAssignment,
                        planId: e.target.value,
                      },
                    }));
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select a plan</em>
                  </MenuItem>

                  {subscriptionPlans?.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "600",
                              textTransform: "capitalize",
                            }}
                          >
                            {plan.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "success.main", fontWeight: "700" }}
                        >
                          {formatCurrency(plan.price)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() =>
              setState((p) => ({ ...p, isAssignPackageDialogOpen: false }))
            }
            variant="outlined"
            sx={{
              color: "#666",
              borderColor: "#ddd",
              textTransform: "none",
              px: 3,
              py: 0.8,
              "&:hover": {
                borderColor: "#ddd",
                backgroundColor: "transparent",
                color: "#666",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              const payload = {
                ...state.packageAssignment,
                planId: Number(state.packageAssignment.planId), // FINAL SAFETY FIX
              };

              handleAssignPackageSubmit(payload);
            }}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: "#000",
              textTransform: "none",
              px: 3,
              py: 0.8,
              "&:hover": {
                bgcolor: "#000",
              },
            }}
          >
            Assign Now
          </Button>
        </DialogActions>
      </Dialog>
      {/* Subscription Plan Dialog */}
      <Dialog
        open={state.planDialog.open}
        onClose={() =>
          setState((p) => ({
            ...p,
            planDialog: { ...p.planDialog, open: false },
          }))
        }
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle sx={{ fontWeight: "800" }}>
          Subscription Plan Editor
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
            <TextField
              label="Plan Name"
              fullWidth
              sx={inputStyle}
              value={state.planDialog.data.name}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  planDialog: {
                    ...p.planDialog,
                    data: { ...p.planDialog.data, name: e.target.value },
                  },
                }))
              }
            />
            <TextField
              label="Price (INR)"
              fullWidth
              type="number"
              sx={inputStyle}
              value={state.planDialog.data.price}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  planDialog: {
                    ...p.planDialog,
                    data: { ...p.planDialog.data, price: e.target.value },
                  },
                }))
              }
            />
            <TextField
              label="Duration (Months)"
              fullWidth
              type="number"
              sx={inputStyle}
              value={state.planDialog.data.duration_months}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  planDialog: {
                    ...p.planDialog,
                    data: {
                      ...p.planDialog.data,
                      duration_months: e.target.value,
                    },
                  },
                }))
              }
            />
            <TextField
              label="Points"
              fullWidth
              type="number"
              sx={inputStyle}
              value={state.planDialog.data.credits}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  planDialog: {
                    ...p.planDialog,
                    data: {
                      ...p.planDialog.data,
                      credits: e.target.value,
                    },
                  },
                }))
              }
            />
            <TextField
              label="Features"
              fullWidth
              multiline
              rows={3}
              sx={inputStyle}
              value={state.planDialog.data.features}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  planDialog: {
                    ...p.planDialog,
                    data: { ...p.planDialog.data, features: e.target.value },
                  },
                }))
              }
            />
            <TextField
              select
              label="Status"
              fullWidth
              sx={inputStyle}
              value={state.planDialog.data.status}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  planDialog: {
                    ...p.planDialog,
                    data: { ...p.planDialog.data, status: e.target.value },
                  },
                }))
              }
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() =>
              setState((p) => ({
                ...p,
                planDialog: { ...p.planDialog, open: false },
              }))
            }
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#ffe599",
              color: "#000",
              borderRadius: "10px",
              fontWeight: "700",
              "&:hover": { bgcolor: "#ffe599" },
            }}
            onClick={() =>
              handlePlanAction(
                state.planDialog.isEdit ? "update" : "insert",
                state.planDialog.data,
              )
            }
          >
            Save Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={state.snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={state.snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "12px" }}
        >
          {state.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
